import { Fragment, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { IconInfoCircle, IconKey, IconPencil, IconUserOff, IconUsers, IconRefresh } from "@tabler/icons-react";
import { ApiConstant, LangConstant } from "const";
import { useUpdateUser, useResetUserPassword, useRole, useUpdateUserAccount } from "hooks";
import { ChangePasswordDialog } from "../components";

const VIEW_MODES = {
  CHANGE_PASSWORD: "changePassword",
  GENERATE_PASSWORD: "generatePassword",
  GRANT_PERMISSION: "grantPermission",
  COPY_PASSWORD: "copyPassword",
};

const UserAction = ({
  data,
  open,
  anchorEl,
  onCloseAction,
  onClickUserInfo,
  onClickUserEdit,
  onOpenPasswordDialog,
}) => {
  const { t: getLabel } = useTranslation();
  const { resetUserPassword } = useResetUserPassword();
  const { isServiceAdmin, isRoot } = useRole();
  const { changePermission } = useUpdateUser();
  const { updateUserAccountAsync } = useUpdateUserAccount();

  const [viewMode, setViewMode] = useState(null);
  const menuRef = useRef(null);

  const handleClickChangePassword = () => {
    setViewMode(VIEW_MODES.CHANGE_PASSWORD);
  };

  const handleCloseAllDialogs = () => {
    setViewMode(null);
    onCloseAction();
  };

  const handleResetPassword = () => {
    if (window.confirm(getLabel(LangConstant.TXT_CONFIRM_ACTION))) {
      resetUserPassword({ id: data.id });
      onCloseAction();
    }
  };

  const handleChangePermission = async (isAdmin = true) => {
    if (window.confirm(getLabel(LangConstant.TXT_CONFIRM_ACTION))) {
      const password = await changePermission(isAdmin, data?.accountId);
      if (password) {
        onOpenPasswordDialog(password);
      }
      onCloseAction();
    }
  };

  const handleChangePassword = async payload => {
    const result = await updateUserAccountAsync(payload);

    if (result.status === ApiConstant.STT_OK) {
      // password available at result.data?.pin
    }
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onCloseAction();
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onCloseAction]);

  // Calculate position from anchorEl - position to the left of the button to avoid right-edge cutoff
  const getMenuStyle = () => {
    if (!anchorEl) return {};
    const rect = anchorEl.getBoundingClientRect();
    const menuWidth = 280;
    const menuHeight = 200; // approximate menu height

    // Horizontal positioning
    let left = rect.left + rect.width / 2 - menuWidth / 2;
    if (left + menuWidth > window.innerWidth - 16) {
      left = window.innerWidth - menuWidth - 16;
    }
    if (left < 16) left = 16;

    // Vertical positioning: open upward if not enough space below
    let top;
    if (rect.bottom + menuHeight + 16 > window.innerHeight) {
      top = rect.top - menuHeight - 4;
      if (top < 16) top = 16;
    } else {
      top = rect.bottom + 4;
    }

    return {
      position: "fixed",
      top,
      left,
      zIndex: 1050,
    };
  };

  return (
    <Fragment>
      {open && (
        <div ref={menuRef} className="dropdown-menu show" style={{ ...getMenuStyle(), width: 280, display: "block", padding: "6px 0" }}>
          {!isServiceAdmin && (
            <button className="dropdown-item d-flex align-items-center gap-3" style={{ padding: "10px 16px" }} onClick={onClickUserInfo}>
              <IconInfoCircle size={20} />
              <span>{getLabel(LangConstant.TXT_INFORMATION)}</span>
            </button>
          )}
          <button className="dropdown-item d-flex align-items-center gap-3" style={{ padding: "10px 16px" }} onClick={onClickUserEdit}>
            <IconPencil size={20} />
            <span>{getLabel(LangConstant.TXT_EDIT_INFO)}</span>
          </button>
          {isServiceAdmin && (
            <button className="dropdown-item d-flex align-items-center gap-3" style={{ padding: "10px 16px" }} onClick={handleClickChangePassword}>
              <IconKey size={20} />
              <span>{getLabel(LangConstant.TXT_CHANGE_PASSWORD)}</span>
            </button>
          )}
          {!isServiceAdmin && (
            <button className="dropdown-item d-flex align-items-center gap-3" style={{ padding: "10px 16px" }} onClick={handleResetPassword}>
              <IconRefresh size={20} />
              <span>{getLabel(LangConstant.TXT_RESET_PASSWORD)}</span>
            </button>
          )}
          {isRoot &&
            (Boolean(data && data.isAdmin) ? (
              <Fragment>
                <button className="dropdown-item d-flex align-items-center gap-3" style={{ padding: "10px 16px" }} onClick={() => handleChangePermission(false)}>
                  <IconUserOff size={20} />
                  <span>{getLabel(LangConstant.TXT_REMOVE_ADMIN_PERMISSION)}</span>
                </button>
                <button className="dropdown-item d-flex align-items-center gap-3" style={{ padding: "10px 16px" }} onClick={() => handleChangePermission(true)}>
                  <IconKey size={20} />
                  <span>{getLabel(LangConstant.TXT_GENERATE_NEW_ADMIN_PASSWORD)}</span>
                </button>
              </Fragment>
            ) : (
              <button className="dropdown-item d-flex align-items-center gap-3" style={{ padding: "10px 16px" }} onClick={() => handleChangePermission(true)}>
                <IconUsers size={20} />
                <span>{getLabel(LangConstant.TXT_GRANT_ADMIN_PERMISSION)}</span>
              </button>
            ))}
        </div>
      )}

      {viewMode === VIEW_MODES.CHANGE_PASSWORD && (
        <ChangePasswordDialog open data={data} onClose={handleCloseAllDialogs} onConfirm={handleChangePassword} />
      )}
    </Fragment>
  );
};

export default UserAction;
